import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import BugsnagPerformance from '@bugsnag/browser-performance';
import React from 'react';
import env from './env';

const ENABLED_RELEASE_STAGES = [
  'staging',
  'production',
  'production-eu',
  'production-au',
  'production-ca',
];

const bugsnag = {
  instance: null as typeof Bugsnag | null,
  ErrorBoundary: null as React.ComponentType<any> | null,

  init() {
    // Determine release stage by combining environment and region
    const getReleaseStage = () => {
      const environment = env.reactAppEnvironment || 'development';
      const region = env.reactAppRegion || 'us';

      if (environment === 'production') {
        return region === 'us' ? 'production' : `production-${region}`;
      }
      return environment;
    };

    // Initialize Bugsnag
    Bugsnag.start({
      apiKey: env.bugsnagApiKey,
      plugins: [new BugsnagPluginReact()],
      releaseStage: getReleaseStage(),
      enabledReleaseStages: ENABLED_RELEASE_STAGES,
      collectUserIp: false,
      appVersion: env.githubSha,
    });

    // Initialize Performance monitoring with simplified callback type
    BugsnagPerformance.start({
      apiKey: env.bugsnagApiKey,
      releaseStage: getReleaseStage(),
      enabledReleaseStages: ENABLED_RELEASE_STAGES,
      appVersion: env.githubSha,
      networkRequestCallback: ((request: any) => {
        const ignoredDomains = [
          'amazonaws',
          'facebook.net',
          'facebook.com',
          'pendo.io',
        ];

        if (
          request.url &&
          ignoredDomains.some((domain) => request.url.includes(domain))
        ) {
          return null;
        }

        if (typeof request.url === 'string') {
          request.url = request.url
            // Replace all numeric fields with :id
            .replace(/\/(\d+)(?=\/|$)/g, (_match: string, id: string) =>
              isNaN(Number(id)) ? `/${id}` : '/:id',
            )
            //Replace generic :id with specifc IDs
            .replace(/\/drafting\/:id/g, '/drafting/:draft_id')
            .replace(/\/form-template\/:id/g, '/form-template/:form_id')
            .replace(
              /\/questionnaires\/:id\/builder/g,
              '/questionnaires/:questionnaires_id/builder',
            )
            .replace(/\/populate\/:id/g, '/populate/:populate_id')
            .replace(/\/templates\/:id\/new/g, '/templates/:template_id/new')
            .replace(/\/matter\/:id/g, '/matter/:matter_id')
            .replace(
              /\/court-forms\/[^/]+\/[^/]+\/form-listing/g,
              '/court-forms/:region/:area/form-listing',
            );
        }
        return request;
      }) as any,
    });

    // Store instance and ErrorBoundary
    this.instance = Bugsnag;
    this.ErrorBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary(React);

    return this;
  },
  setUser(userId: string | number) {
    if (this.instance && userId) {
      this.instance.setUser(`${userId}`);
    }
  },
};

bugsnag.init();

export default bugsnag;
